/**
 * A text link. Three looks: `inline` sits in a sentence and is always underlined, so colour is
 * never the only sign it is a link; `standalone` stands on its own line and underlines on hover;
 * `subtle` is a secondary link in meta text.
 *
 * `navigate` hands an internal link to the app's router so a click does not reload the page. A
 * click with a modifier key or a middle button is left to the browser, which opens a new tab
 * better than the app could. `external` opens in a new tab, says so to a screen reader and with an
 * icon, and sends no referrer.
 *
 * A visited link looks the same as an unvisited one. The links in these apps lead to records and
 * settings, where "visited" means nothing.
 */
import * as stylex from "@stylexjs/stylex";
import type { MouseEvent, ReactNode } from "react";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { focusRing } from "./focus.ts";
import { ExternalIcon } from "./icons.tsx";
import { visuallyHidden } from "./visuallyHidden.ts";
const styles = {
  base: {
    kaIpWk: "x8wwnw1",
    kcSHmL: "x1ohr1zr",
    kNySMw: "x1kuah0p",
    k1ekBW: "xt3l3uh",
    kIyJzY: "x149xavy",
    $$css: true
  },
  inline: {
    kMwMTN: "x1m7e9bc x1fn7bfw",
    kMnn75: "xujl8zx",
    $$css: true
  },
  standalone: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xskm3c9",
    kAzted: "xv75w1r xlivvtk xei8rr2",
    kMwMTN: "x1m7e9bc x1fn7bfw",
    k63SB2: "xqu410z",
    kMnn75: "xkrqix3 x1sur9pj",
    $$css: true
  },
  subtle: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kAzted: "xv75w1r xlivvtk xei8rr2",
    kMwMTN: "x1lm7cez x1fn7bfw",
    kMnn75: "xkrqix3 x1sur9pj",
    $$css: true
  },
  icon: {
    k1xSpc: "x3nfvp2",
    keTefX: "xe7k0pl",
    $$css: true
  }
};
export type LinkVariant = "inline" | "standalone" | "subtle";

/** Hands a same-site path to the app's router. */
export type Navigate = (href: string) => void;

/**
 * The click handler every in-app link shares: a plain left click goes through the router, and any
 * other click is left to the browser.
 */
export function followWith(navigate: Navigate | undefined, href: string) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (navigate === undefined) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    navigate(href);
  };
}
export interface LinkProps {
  readonly href: string;
  readonly children: ReactNode;
  readonly variant?: LinkVariant;
  /** Opens in a new tab with no referrer, and says so. */
  readonly external?: boolean;
  /** The app's router, for a link inside the app. */
  readonly navigate?: Navigate | undefined;
  readonly testId?: string;
}
export function Link({
  href,
  children,
  variant = "inline",
  external = false,
  navigate,
  testId
}: LinkProps) {
  if (external) return <a href={href} target="_blank" rel="noreferrer" data-testid={testId} {...stylex.props(styles.base, styles[variant], focusRing)}>
        {children}
        <span {...stylex.props(visuallyHidden.hidden)}> (opens in a new tab)</span>
        <span className="x3nfvp2 xe7k0pl">
          <ExternalIcon />
        </span>
      </a>;
  return <a href={href} onClick={followWith(navigate, href)} data-testid={testId} {...stylex.props(styles.base, styles[variant], focusRing)}>
      {children}
    </a>;
}